import { updatePushDiagnostics } from "./users";
import axiosInstance from "./axiosInstance";

const PENDING_ACK_KEY = "pendingPushAcks";

function detectPushPlatform(userAgent = navigator.userAgent || "") {
  const ua = String(userAgent).toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  if (/mobile/.test(ua)) return "mobile";
  return "desktop";
}

function getBrowserNotificationPermission() {
  if (typeof Notification === "undefined") {
    return "unsupported";
  }
  return Notification.permission || "unknown";
}

function isRunningAsStandalonePwa() {
  if (typeof window === "undefined") return false;
  const standaloneMedia = window.matchMedia?.(
    "(display-mode: standalone)",
  )?.matches;
  const iosStandalone = window.navigator?.standalone === true;
  return Boolean(standaloneMedia || iosStandalone);
}

function isPushApiSupported() {
  if (typeof window === "undefined") return false;
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    Boolean(window.isSecureContext)
  );
}

function computeSubscriptionStatus({
  permission,
  platform,
  isStandalone,
  hasToken,
} = {}) {
  if (!isPushApiSupported() || permission === "unsupported") {
    return "not_supported";
  }
  if (platform === "ios" && isStandalone === false) {
    return "needs_pwa";
  }
  if (permission === "denied") return "permission_denied";
  if (permission === "default") return "permission_default";
  if (permission === "granted" && !hasToken) return "token_missing";
  if (permission === "granted" && hasToken) return "subscribed";
  return "unknown";
}

export function computePushDiagnosis(user = {}) {
  const diagnostics = user.pushDiagnostics || {};
  const optIn = Boolean(
    user.receiveAnomalyPush ||
      user.recieveCharacterizationPush ||
      user.recieveQualicheckPush ||
      user.receiveFillingPush ||
      user.receiveRotationPush,
  );
  const tokenCount =
    typeof user.fcmTokenCount === "number"
      ? user.fcmTokenCount
      : Array.isArray(user.fcmTokens)
        ? user.fcmTokens.length
        : 0;
  const permission = diagnostics.permission || "unknown";
  const platform = diagnostics.platform || "unknown";
  const isStandalone = diagnostics.isStandalone;
  const subscriptionStatus =
    diagnostics.subscriptionStatus ||
    computeSubscriptionStatus({
      permission,
      platform,
      isStandalone,
      hasToken: tokenCount > 0,
    });

  if (!optIn) return { code: "opt_in_off", severity: "info" };
  if (subscriptionStatus === "not_supported" || permission === "unsupported") {
    return { code: "not_supported", severity: "ko" };
  }
  if (
    subscriptionStatus === "needs_pwa" ||
    (platform === "ios" && isStandalone === false)
  ) {
    return { code: "needs_pwa", severity: "warn" };
  }
  if (subscriptionStatus === "permission_denied" || permission === "denied") {
    return { code: "permission_denied", severity: "ko" };
  }
  if (subscriptionStatus === "token_missing" || tokenCount <= 0) {
    return { code: "token_missing", severity: "warn" };
  }
  if (
    diagnostics.lastPushFailedAt &&
    (!diagnostics.lastPushAcceptedAt ||
      new Date(diagnostics.lastPushFailedAt) >
        new Date(diagnostics.lastPushAcceptedAt))
  ) {
    return { code: "send_failed", severity: "ko" };
  }
  if (diagnostics.lastPushAcceptedAt && !diagnostics.lastPushDeliveredAt) {
    return { code: "accepted_not_delivered", severity: "warn" };
  }
  if (subscriptionStatus === "subscribed" || tokenCount > 0) {
    return { code: "ok", severity: "ok" };
  }
  return { code: "unknown", severity: "neutral" };
}

function readPendingAcks() {
  try {
    const raw = localStorage.getItem(PENDING_ACK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingAcks(acks) {
  try {
    localStorage.setItem(PENDING_ACK_KEY, JSON.stringify(acks.slice(-20)));
  } catch {
    // ignore quota errors
  }
}

function queuePendingAck(body) {
  const key = `${body.notificationId}:${body.tokenHash}:${body.event}`;
  const existing = readPendingAcks().filter(
    (item) => `${item.notificationId}:${item.tokenHash}:${item.event}` !== key,
  );
  existing.push({ ...body, queuedAt: Date.now() });
  writePendingAcks(existing);
}

/**
 * Flush queued acks (fallback when SW could not reach API, e.g. localhost).
 */
export async function flushPendingPushAcks() {
  const pending = readPendingAcks();
  if (pending.length === 0) return;

  const remaining = [];
  for (const body of pending) {
    try {
      await axiosInstance.post("notification/push-ack", {
        notificationId: body.notificationId,
        userId: body.userId,
        tokenHash: body.tokenHash,
        ackToken: body.ackToken,
        event: body.event,
      });
    } catch {
      remaining.push(body);
    }
  }
  writePendingAcks(remaining);
}

/**
 * Report current PWA push capability to the backend (Phase 1 diagnostics).
 */
export async function reportPushDiagnostics() {
  try {
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const platform = detectPushPlatform(userAgent);
    const permission = getBrowserNotificationPermission();
    const isStandalone = isRunningAsStandalonePwa();
    const hasToken = Boolean(
      typeof document !== "undefined" && document.cookie.includes("fcmToken="),
    );
    const subscriptionStatus = computeSubscriptionStatus({
      permission: isPushApiSupported() ? permission : "unsupported",
      platform,
      isStandalone,
      hasToken,
    });

    await updatePushDiagnostics({
      permission: isPushApiSupported() ? permission : "unsupported",
      userAgent,
      platform,
      isStandalone,
      subscriptionStatus,
    });
  } catch (error) {
    console.error("Failed to report push diagnostics:", error);
  }
}

/**
 * Ack FCM delivery/open — tries app API first, then payload ackUrl, then queues.
 */
export async function acknowledgePushEvent(payload, event) {
  const data = payload?.data || payload || {};
  const { notificationId, userId, tokenHash, ackToken, ackUrl } = data;
  if (!notificationId || !userId || !tokenHash || !ackToken) {
    return;
  }

  const body = {
    notificationId,
    userId,
    tokenHash,
    ackToken,
    event,
  };

  // 1) Authenticated/app API base (works when PWA can reach configured API)
  try {
    await axiosInstance.post("notification/push-ack", body);
    await flushPendingPushAcks();
    return;
  } catch {
    // continue
  }

  // 2) Absolute ackUrl embedded in FCM payload
  if (ackUrl) {
    try {
      const response = await fetch(ackUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      });
      if (response.ok) {
        return;
      }
    } catch {
      // continue
    }
  }

  // 3) Queue for later retry when app is online against a reachable API
  queuePendingAck(body);
}
